/**
 * Account screen state — wallet balance, coin balance, the signed-in user's other roles, the active-role console
 * tile, "My consoles" (with the super-admin guard and de-duplication) and role switching. Moved verbatim out of
 * ProfilePage so the classic view and the Blinkit view share one implementation.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenant } from "../../../contexts/TenantContext";
import { api } from "../../../lib/api";
import { consoleInBuild } from "../../../lib/buildLock";
import { Bike, Network, ShieldCheck, Store } from "lucide-react";

export const CONSOLE_BY_ROLE = {
    TENANT_ADMIN: { Icon: ShieldCheck, label: "Tenant Admin Console", to: "/admin" },
    SUPER_ADMIN: { Icon: ShieldCheck, label: "Super Admin Console", to: "/super" },
    RESTAURANT_OWNER: { Icon: Store, label: "Merchant Console", to: "/merchant" },
    ZONE_ADMIN: { Icon: Network, label: "Zone Admin Console", to: "/zone-admin" },
    RIDER: { Icon: Bike, label: "Rider Console", to: "/rider" },
};

export function useProfileController() {
    const { user, logout } = useAuth();
    const { tenant, lang } = useTenant();
    const nav = useNavigate();
    const [wallet, setWallet] = useState(null);
    const [coinsBal, setCoinsBal] = useState(null);
    const [myRoles, setMyRoles] = useState([]);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try { const { data } = await api.get("/wallet"); if (!cancelled) setWallet(data.wallet); } catch (err) { console.debug("[Profile] /wallet failed:", err); }
            // Coins live in the SEPARATE ``coins_balances`` collection
            // (not ``wallet.coins``, which is a legacy field that's
            // always 0). Fetch the real balance so the profile card
            // can show the customer their actual earn/redeem total.
            try { const { data } = await api.get("/customer/coins"); if (!cancelled) setCoinsBal(data); } catch (err) { console.debug("[Profile] /customer/coins failed:", err); }
            try { const { data } = await api.get("/auth/my-roles"); if (!cancelled) setMyRoles(data.roles || []); } catch (err) { console.debug("[Profile] /auth/my-roles failed:", err); }
        })();
        return () => { cancelled = true; };
    }, [user]);

    if (!user) {
        return { user, logout, tenant, lang, nav, wallet, coinsBal, myRoles };
    }
    // Show the wallet/coins card whenever EITHER datasource has loaded —
    // wallet balance lives in ``/wallet`` and coin balance in
    // ``/customer/coins`` (a separate collection). Either can come back
    // with zero and we still want to render the card so the user sees
    // their (zero) totals; only hide it when BOTH have failed.
    const coinsValue = coinsBal?.balance ?? wallet?.coins ?? 0;
    const showWalletCard = wallet || coinsBal;

    // Active-role console — if the logged-in user is currently an
    // admin / staff role, show a prominent "Open your console" tile at
    // the top of the profile so they can hop back to the dashboard with
    // one tap. This replaces the user-confusion of "where's my console
    // switch button?" — that button is hidden by design for the role
    // they're already in, so we show a dedicated CTA instead.
    const activeConsole = CONSOLE_BY_ROLE[user.role];

    // Role-locked builds (iter50) — Customer-only APK must not show any
    // "Open Rider/Merchant/Admin console" tile, because the destination
    // routes are stripped from this bundle. Apple/Google reviewers
    // would flag dead deep-links as deceptive.
    // iter134g — Show My Consoles on APK too. Previously suppressed
    // via ``!ROLE_LOCK`` which hid the admin/rider switch cards on
    // the Customer APK. Now that admin routes ship in the
    // CUSTOMER_OR_MERCHANT bundle (see buildLock SHOW_ADMIN) it's
    // safe + wanted to render the tiles here.
    const showRoleSwitchers = true;

    // Roles OTHER than the one currently in session — power the "My Consoles" card.
    // Two layers of defence:
    //   1. NEVER show SUPER_ADMIN tile unless this mobile IS the canonical
    //      super-admin mobile (matches the backend's hard gate).
    //   2. Dedupe by `(role, tenant_id)` so a stale duplicate user row
    //      in the DB doesn't render the same console twice.
    const SUPER_ADMIN_MOBILE = "9858558555";
    const seen = new Set();
    const otherRoles = !showRoleSwitchers ? [] : (myRoles || []).filter((r) => {
        if (r.role === user.role && r.tenant_id === user.tenant_id) return false;
        if (!CONSOLE_BY_ROLE[r.role]) return false;
        if (!consoleInBuild(r.role)) return false;       // iter181 — no tile for a console this build doesn't ship
        if (r.role === "SUPER_ADMIN" && user.mobile_number !== SUPER_ADMIN_MOBILE) return false;
        const key = `${r.role}|${r.tenant_id || "_"}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    const switchTo = async (target) => {
        try {
            await api.post("/auth/switch-role", {
                role: target.role,
                tenant_slug: target.tenant_slug || undefined,
            });
            toast.success(`Switched to ${target.role.replaceAll("_", " ").toLowerCase()}`);
            // iter66 — Hard navigation. The server has already set the
            // new httpOnly cookies; reloading the page lets AuthContext
            // bootstrap cleanly from those cookies and avoids the
            // transient-state race where the destination layout briefly
            // sees the new user role on the OLD URL and bounces to /login.
            const cfg = CONSOLE_BY_ROLE[target.role];
            window.location.assign(cfg?.to || "/");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Switch failed");
        }
    };

    return {
        user, logout, tenant, lang, nav, wallet, coinsBal, myRoles, coinsValue, showWalletCard, activeConsole,
        otherRoles, switchTo,
    };
}
